/* @flow */

import * as React from 'react';

import classnames from 'classnames';
import {motion} from 'framer-motion';

import {colors} from '../colors';

import './switch.module.css';

type Props = {|
    isOn: boolean,
    onChange: (e: SyntheticEvent<HTMLDivElement>) => void,
    isDisabled?: boolean,
    isRounded?: boolean,
    shouldAnimate?: boolean,
    isSmall?: boolean,
    // If true, the onChange event will not bubble up to the parent
    shouldStopPropagation?: boolean,
    tooltipText?: string,
|};

const containerVariants = {
    active: {
        backgroundColor: colors.blue,
        color: '#ffffff',
        borderColor: colors.blue,
    },
    inactive: {
        backgroundColor: colors.offWhite,
        color: colors.grey,
        borderColor: colors.offWhiteDk,
    },
};

const getSliderVariants = (isRounded, isSmall) => {
    let activeLeft;
    if (isSmall) {
        activeLeft = isRounded ? 18 : 20;
    } else {
        activeLeft = isRounded ? 20 : 59;
    }

    const baseVariants = {
        active: {
            left: activeLeft,
            width: isRounded && (isSmall ? 13 : 18),
            height: isRounded && (isSmall ? 13 : 18),
            top: isRounded && (isSmall ? 2 : 1),
            backgroundColor: isRounded && colors.white,
        },
        inactive: {
            left: isRounded ? 4 : 6,
            backgroundColor: isRounded ? colors.formBorder : colors.white,
            width: isRounded && (isSmall ? 12 : 12),
            height: isRounded && (isSmall ? 11 : 11),
            top: isRounded && (isSmall ? 2 : 4),
        },
    };

    return baseVariants;
};

/**
 * Use this to indicate to a user if a setting is on or off,
 * and allow them to change the value. Its basically a really fancy checkbox.
 */
export function Switch(props: Props) {
    const {
        isOn,
        isDisabled,
        isRounded,
        isSmall,
        onChange,
        shouldAnimate = true,
        shouldStopPropagation = false,
    } = props;

    // Track if component has mounted to prevent initial animation
    const [hasMounted, setHasMounted] = React.useState(false);

    React.useEffect(() => {
        setHasMounted(true);
    }, []);

    const styleNames = classnames({
        container: true,
        'container--disabled': isDisabled,
        'container--disabled-inactive': isDisabled && !isOn,
        'container--round': isRounded,
        'container--small': isSmall,
    });

    // Get the current styles based on state (disabled is independent of active/inactive)
    const variantKey = isOn ? 'active' : 'inactive';
    const currentContainerStyle = containerVariants[variantKey];
    const sliderVariants = getSliderVariants(isRounded, isSmall);
    const currentSliderStyle = sliderVariants[variantKey];

    const handleClick = (e: SyntheticEvent<HTMLDivElement>) => {
        if (isDisabled) {
            return;
        }

        if (shouldStopPropagation) {
            e.stopPropagation();
        }

        onChange(e);
    };

    return shouldAnimate ? (
        <motion.div
            onClick={handleClick}
            styleName={styleNames}
            disabled={isDisabled}
            animate={hasMounted ? variantKey : false}
            variants={containerVariants}
            initial={variantKey}
            data-tip={props.tooltipText}
            tabIndex={isDisabled ? -1 : 0}
        >
            <div styleName='inner-container'>
                {!isRounded ? (
                    <>
                        <div styleName='label'>On</div>
                        <div styleName='label'>Off</div>
                    </>
                ) : (
                    <div style={{width: isSmall ? '33px' : '40px'}} />
                )}
            </div>
            <motion.div
                styleName={isRounded ? 'slider--round' : 'slider'}
                animate={hasMounted ? variantKey : false}
                variants={sliderVariants}
                initial={variantKey}
                transition={{type: 'tween', ease: 'easeInOut', duration: 0.2}}
            />
        </motion.div>
    ) : (
        <div
            onClick={handleClick}
            styleName={styleNames}
            style={currentContainerStyle}
            data-tip={props.tooltipText}
        >
            <div styleName='inner-container'>
                {!isRounded ? (
                    <>
                        <div styleName='label'>On</div>
                        <div styleName='label'>Off</div>
                    </>
                ) : (
                    <div style={{width: isSmall ? '33px' : '40px'}} />
                )}
            </div>
            <div styleName={isRounded ? 'slider--round' : 'slider'} style={currentSliderStyle} />
        </div>
    );
}
